import { type ReactNode } from "react";

import { Provider } from "~/services";
import { Chip } from "~/ui/common/chip";
import { customIcons, icons } from "~/ui/common/Icons";
import { ResponsiveModal } from "~/ui/common/Modal/ResponsiveModal";

type ProviderProfileModalProps = {
  provider: Provider;
  show: boolean;
  onClose: () => void;
};

const ProfileSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="text-indigo-charcoal space-y-3 text-base">
    <h3 className="text-sm font-medium uppercase tracking-wider">{title}</h3>
    {children}
  </section>
);

export const ProviderProfileModal = ({
  provider,
  show,
  onClose,
}: ProviderProfileModalProps) => {
  const {
    name,
    title,
    image,
    description,
    languages,
    carePhilosophy,
    additionalExpertise,
    testimonials,
    firstName,
  } = provider;

  return (
    <ResponsiveModal show={show} onClose={onClose} title={name} hideTitle>
      <div className="space-y-8">
        <div className="border-light-lavender-gray flex flex-col items-center gap-2 border-b border-t pb-4 pt-6 text-center md:border-t-0 md:pt-2">
          <div className="size-22 flex items-center justify-center overflow-hidden rounded-full bg-gray-200 md:size-32">
            {image ? (
              <img
                src={image}
                alt={name}
                className="h-full w-full object-cover"
              />
            ) : (
              <icons.outline.UserIcon className="size-12 text-gray-500 md:size-16" />
            )}
          </div>
          <h2 className="font-ivy text-indigo-charcoal text-[32px]">
            {name}
            {title && `, ${title}`}
          </h2>
        </div>

        {description && (
          <ProfileSection title="Background">
            <p>{description}</p>
          </ProfileSection>
        )}

        {languages?.length > 0 && (
          <ProfileSection title="Languages spoken">
            <div className="flex flex-wrap gap-2">
              {languages.map((language) => (
                <Chip key={language} type="lavender">
                  {language}
                </Chip>
              ))}
            </div>
          </ProfileSection>
        )}

        {carePhilosophy && (
          <ProfileSection title="Care philosophy">
            <blockquote className="flex gap-2 italic">
              <customIcons.QuoteMarkIcon
                className="text-indigo-charcoal h-4 w-auto shrink-0"
                aria-hidden="true"
              />
              {carePhilosophy}
            </blockquote>
          </ProfileSection>
        )}

        {additionalExpertise?.length > 0 && (
          <ProfileSection title="Additional expertise">
            <ul className="space-y-4">
              {additionalExpertise.map((expertise) => (
                <li key={expertise} className="flex items-center gap-3">
                  <span className="bg-placebo-lavender mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full">
                    <icons.outline.CheckIcon className="text-violet stroke-3 size-3" />
                  </span>
                  {expertise}
                </li>
              ))}
            </ul>
          </ProfileSection>
        )}

        {testimonials?.length > 0 && (
          <ProfileSection title={`Hear from ${firstName ?? name}'s patients`}>
            <div className="space-y-3">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial}
                  className="bg-placebo-lavender rounded-xl p-4"
                >
                  &ldquo;{testimonial}&rdquo;
                </div>
              ))}
            </div>
          </ProfileSection>
        )}
      </div>
    </ResponsiveModal>
  );
};
